import React, { useState, useEffect } from 'react';
import { X, Shield, CheckCircle, XCircle, Lock, Unlock, AlertTriangle, Clock, UserCheck, Save } from 'lucide-react';
import { User as UserType } from '../../types';
import { format } from 'date-fns';
import { tr } from 'date-fns/locale';
import { supabase } from '../../lib/supabase';

interface UserSecurityModalProps {
  isOpen: boolean;
  user: UserType | null;
  onClose: () => void;
  onUpdate: () => void;
}

const AVAILABLE_PERMISSIONS = [
  { id: 'view_students', label: 'Öğrencileri Görüntüleme' },
  { id: 'edit_students', label: 'Öğrenci Düzenleme' },
  { id: 'view_reports', label: 'Raporları Görüntüleme' },
  { id: 'manage_units', label: 'Birimleri Yönetme' },
  { id: 'add_notes', label: 'Not Ekleme' },
  { id: 'schedule_meetings', label: 'Görüşme Planlama' },
  { id: 'manage_documents', label: 'Belge Yönetimi' },
  { id: 'track_applications', label: 'Başvuru Takibi' }
];

const UserSecurityModal: React.FC<UserSecurityModalProps> = ({
  isOpen,
  user,
  onClose,
  onUpdate
}) => {
  const [permissions, setPermissions] = useState<string[]>([]);
  const [isActive, setIsActive] = useState<boolean>(true);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (isOpen && user) {
      setPermissions(user.permissions || []);
      setIsActive(user.isActive !== undefined ? user.isActive : true);
    }
  }, [isOpen, user]);

  if (!isOpen || !user) return null;

  const handlePermissionChange = (permissionId: string, checked: boolean) => {
    if (checked) {
      setPermissions(prev => [...prev, permissionId]);
    } else {
      setPermissions(prev => prev.filter(p => p !== permissionId));
    }
  };

  const handleToggleStatus = async () => {
    try {
      const nextStatus = !isActive;
      setIsSaving(true);
      const { data, error } = await supabase.functions.invoke('manage-user-status', {
        body: { userId: user.id, email: user.email, isActive: nextStatus },
      });

      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      setIsActive(nextStatus);
      alert(`Kullanıcı hesabı ${nextStatus ? 'aktifleştirildi' : 'donduruldu'}.`);
      await onUpdate();
    } catch (e: any) {
      console.error('Toggle status error:', e);
      alert(e?.message || 'Hesap durumu güncellenemedi.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleResetPassword = async () => {
    if (await window.appConfirm('Bu kullanıcının şifresini sıfırlamak istediğinize emin misiniz?')) {
      try {
        if (supabase) {
          await supabase.auth.resetPasswordForEmail(user.email);
        }
        alert('Şifre sıfırlama linki kullanıcının e-posta adresine gönderildi.');
      } catch (e) {
        console.error('Reset password error:', e);
      }
    }
  };

  const handleSavePermissions = async () => {
    try {
      setIsSaving(true);
      if (supabase) {
        await supabase.from('profiles').update({ permissions }).eq('id', user.id);
      }
      alert('Kullanıcı yetkileri başarıyla güncellendi.');
      onUpdate();
      onClose();
    } catch (e) {
      console.error('Save permissions error:', e);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 dark:bg-black/80 backdrop-blur-sm p-4">
      <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-800">
          <div className="flex items-center space-x-3">
            <Shield className="w-8 h-8 text-purple-600 dark:text-purple-400" />
            <div>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Güvenlik Yönetimi</h2>
              <p className="text-sm text-gray-600 dark:text-gray-400">{user.name} ({user.email})</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full transition-colors"
          >
            <X className="w-5 h-5 text-gray-500 dark:text-gray-400" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Account Status */}
          <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-6 border border-gray-200/80 dark:border-gray-700/80">
            <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-4">Hesap Durumu</h3>
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                {isActive ? (
                  <CheckCircle className="w-6 h-6 text-green-500" />
                ) : (
                  <XCircle className="w-6 h-6 text-red-500" />
                )}
                <div>
                  <p className="font-medium text-gray-900 dark:text-white">
                    {isActive ? 'Hesap Aktif' : 'Hesap Pasif'}
                  </p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {isActive ? 'Kullanıcı sisteme giriş yapabilir' : 'Kullanıcı sisteme giriş yapamaz'}
                  </p>
                </div>
              </div>
              <button
                onClick={handleToggleStatus}
                disabled={isSaving}
                className={`flex items-center space-x-2 px-4 py-2 rounded-xl text-sm font-semibold transition-colors ${
                  isActive
                    ? 'bg-red-600 hover:bg-red-700 text-white'
                    : 'bg-green-600 hover:bg-green-700 text-white'
                } disabled:cursor-not-allowed disabled:opacity-50`}
              >
                {isActive ? <Lock className="w-4 h-4" /> : <Unlock className="w-4 h-4" />}
                <span>{isActive ? 'Hesabı Dondur' : 'Hesabı Aktifleştir'}</span>
              </button>
            </div>
          </div>

          {/* Password Management */}
          <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-6 border border-gray-200/80 dark:border-gray-700/80">
            <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-4">Şifre Sıfırlama</h3>
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium text-gray-900 dark:text-white">Şifre Sıfırlama E-postası</p>
                <p className="text-sm text-gray-600 dark:text-gray-400">Kullanıcının adresine güvenli sıfırlama bağlantısı gönder</p>
              </div>
              <button
                onClick={handleResetPassword}
                className="flex items-center space-x-2 bg-amber-600 hover:bg-amber-700 text-white px-4 py-2 rounded-xl text-sm font-semibold transition-colors"
              >
                <AlertTriangle className="w-4 h-4" />
                <span>E-posta Gönder</span>
              </button>
            </div>
          </div>

          {/* Permissions */}
          {user.role !== 'admin' && (
            <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-6 border border-gray-200/80 dark:border-gray-700/80">
              <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-4">Kullanıcı Yetkileri</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {AVAILABLE_PERMISSIONS.map(permission => (
                  <label key={permission.id} className="flex items-center p-2 rounded-lg bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 cursor-pointer">
                    <input
                      type="checkbox"
                      id={permission.id}
                      checked={permissions.includes(permission.id)}
                      onChange={(e) => handlePermissionChange(permission.id, e.target.checked)}
                      className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                    />
                    <span className="ml-2 text-xs font-medium text-gray-900 dark:text-gray-300">
                      {permission.label}
                    </span>
                  </label>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200 dark:border-gray-800 bg-gray-50/50 dark:bg-gray-800/50">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 transition-colors"
          >
            İptal
          </button>
          {user.role !== 'admin' && (
            <button
              onClick={handleSavePermissions}
              disabled={isSaving}
              className="flex items-center space-x-2 bg-purple-600 hover:bg-purple-700 text-white px-5 py-2 rounded-xl text-sm font-semibold transition-colors disabled:opacity-50"
            >
              <Save className="w-4 h-4" />
              <span>{isSaving ? 'Kaydediliyor...' : 'Yetkileri Kaydet'}</span>
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserSecurityModal;
